import { BoostProvider, BoostTask } from '../../types/boost';

interface ShowPromiseResult {
    done: boolean;
    description: string;
    state: 'load' | 'render' | 'playing' | 'destroy';
    error: boolean;
}

interface AdsgramSDK {
    init: (config: { blockId: string }) => {
        show: () => Promise<ShowPromiseResult>;
    };
}

declare global {
    interface Window {
        Adsgram: AdsgramSDK;
    }
}

export class AdsgramBoostProvider implements BoostProvider {
    private static BLOCK_ID = import.meta.env.VITE_ADSGRAM_BLOCK_ID;
    private isInitialized = false;
    private initializationPromise: Promise<void> | null = null;
    private adController: any = null;

    getName(): string {
        return 'Adsgram';
    }

    async getAvailableTasks(): Promise<BoostTask[]> {
        try {
            await this.initialize();
            
            // Если SDK инициализирован успешно, возвращаем одно задание
            if (this.isInitialized) {
                return [{
                    id: 'adsgram-1',
                    title: 'Просмотреть рекламу',
                    description: 'Посмотрите рекламу для получения буста',
                    provider: this.getName(),
                    onClick: async () => {
                        return await this.showAd();
                    }
                }];
            }
            
            return [];
        } catch (error) {
            console.error('Ошибка получения заданий Adsgram:', error);
            return [];
        }
    }

    private async initialize(): Promise<void> {
        if (this.isInitialized) return;
        
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        try {
            this.initializationPromise = (async () => {
                if (!AdsgramBoostProvider.BLOCK_ID) {
                    throw new Error('BLOCK_ID Adsgram не найден в переменных окружения');
                }

                // Ждем загрузки SDK
                await this.waitForSDK();
                console.log('Adsgram SDK перед инициализацией:', window.Adsgram);

                // Инициализируем SDK
                this.adController = window.Adsgram.init({ 
                    blockId: AdsgramBoostProvider.BLOCK_ID 
                });
                
                this.isInitialized = true;
                console.log('Adsgram SDK успешно инициализирован');
            })();

            await this.initializationPromise;
        } catch (error) {
            console.error('Ошибка инициализации Adsgram SDK:', error);
            this.isInitialized = false;
            throw error;
        } finally {
            this.initializationPromise = null;
        }
    }

    private waitForSDK(timeout = 2000): Promise<void> {
        return new Promise((resolve, reject) => {
            const start = Date.now();
            
            const check = () => {
                if (window.Adsgram) {
                    console.log('Adsgram SDK найден');
                    resolve();
                } else if (Date.now() - start > timeout) {
                    reject(new Error('Таймаут ожидания загрузки Adsgram SDK'));
                } else {
                    setTimeout(check, 100);
                }
            };
            
            check();
        });
    }

    async startTask(): Promise<'success' | 'user_cancelled' | 'ads_not_found'> {
        return this.showAd();
    }

    private async showAd(): Promise<'success' | 'user_cancelled' | 'ads_not_found'> {
        try {
            if (!this.adController) {
                console.error('Adsgram контроллер не инициализирован');
                return 'ads_not_found';
            }

            const result = await this.adController.show();
            
            if (result.done) {
                console.log('Реклама Adsgram успешно показана');
                return 'success';
            }

            if (result.error) {
                console.error('Ошибка при показе рекламы Adsgram:', result.description);
                return 'ads_not_found';
            }

            console.log(`Неожиданный результат показа рекламы Adsgram: ${result.description} (состояние: ${result.state})`);
            return 'ads_not_found';
        } catch (error) {
            console.error('Ошибка при показе рекламы Adsgram:', error);
            return 'ads_not_found';
        }
    }
} 